import { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { ParsedLibraryItem } from '@/lib/uploadConstants';
import { UNIVERSAL_CATEGORIES } from '@/lib/libraryTaxonomy';

interface ReviewItem extends ParsedLibraryItem {
  selected: boolean;
}

interface KnowledgeReviewListProps {
  items: ParsedLibraryItem[];
  onImport: (selectedItems: ParsedLibraryItem[]) => void;
  onSkip: () => void;
  isImporting: boolean;
}

export function KnowledgeReviewList({ items, onImport, onSkip, isImporting }: KnowledgeReviewListProps) {
  const [reviewItems, setReviewItems] = useState<ReviewItem[]>(() =>
    items.map(item => ({ ...item, selected: true }))
  );

  const selectedItems = useMemo(() => reviewItems.filter(i => i.selected), [reviewItems]);
  const lowCount = useMemo(() => reviewItems.filter(i => i.confidence < 0.6).length, [reviewItems]);
  const dupCount = 0; // Duplicate checking is skipped in onboarding for speed

  const toggleItem = (id: string) => {
    setReviewItems(prev => prev.map(i => i.id === id ? { ...i, selected: !i.selected } : i));
  };

  const deselectLowConfidence = useCallback(() => {
    setReviewItems(prev => prev.map(i => i.confidence < 0.6 ? { ...i, selected: false } : i));
  }, []);

  const selectAll = useCallback((selected: boolean) => {
    setReviewItems(prev => prev.map(i => ({ ...i, selected })));
  }, []);

  const handleImport = () => {
    onImport(selectedItems);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="border border-border rounded-2xl overflow-hidden"
    >
      {/* Header with bulk actions */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/50 bg-muted/20">
        <span className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{selectedItems.length}</span> of {reviewItems.length} items selected
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => selectAll(true)}
            className="text-[10px] px-2 py-0.5 border border-border rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            Select all
          </button>
          {lowCount > 0 && (
            <button
              onClick={deselectLowConfidence}
              className="text-[10px] px-2 py-0.5 border border-border rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              Deselect low confidence
            </button>
          )}
        </div>
      </div>

      {/* Item list */}
      <div className="max-h-[240px] overflow-y-auto">
        {reviewItems.map((item, index) => {
          const confidencePct = Math.round(item.confidence * 100);
          const confidenceColor = confidencePct >= 75
            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
            : confidencePct >= 45
              ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
              : 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20';
          const confidenceLabel = confidencePct >= 75 ? 'High' : confidencePct >= 45 ? 'Med' : 'Low';

          const categoryLabel = UNIVERSAL_CATEGORIES.find(c => c.value === item.universalCategory)?.label
            || item.suggested_category;

          return (
            <div
              key={item.id}
              className={cn(
                'flex items-start gap-2.5 px-4 py-2.5',
                index < reviewItems.length - 1 && 'border-b border-border/30',
                'hover:bg-muted/20 transition-colors',
                !item.selected && 'opacity-40'
              )}
            >
              <Checkbox
                checked={item.selected}
                onCheckedChange={() => toggleItem(item.id)}
                className="h-[14px] w-[14px] mt-0.5 shrink-0 rounded-[3px] data-[state=checked]:bg-[hsl(var(--accent-orange))] data-[state=checked]:border-[hsl(var(--accent-orange))]"
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground truncate">{item.title}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className={cn('text-[9px] font-medium px-1.5 py-px rounded-[3px] border', confidenceColor)}>
                    {confidenceLabel}
                  </span>
                  <span className="text-[9px] px-1.5 py-px rounded-[3px] bg-muted border border-border/30 text-muted-foreground">
                    {categoryLabel}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
        <button
          onClick={onSkip}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Skip for now →
        </button>
        <Button
          onClick={handleImport}
          disabled={selectedItems.length === 0 || isImporting}
          size="sm"
          className="text-xs h-8 gap-1.5 bg-[hsl(var(--accent-orange))] hover:bg-[hsl(var(--accent-orange-hover))] text-white shadow-lg shadow-[hsl(var(--accent-orange))]/25"
        >
          {isImporting ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              Importing...
            </>
          ) : (
            <>
              Import {selectedItems.length} items
              <ArrowRight className="h-3 w-3" />
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
}
